<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem/MenuItem'
import { PropType, ref } from 'vue'

defineProps({
  lists: {
    type: Array as PropType<Array<MenuItem>>,
    default: () => []
  },
  title: {
    type: String,
    required: true,
    default: 'Menu'
  }
})

const showSubmenu = ref(false)
const toggleSubmenu = () => {
  showSubmenu.value = !showSubmenu.value
}
</script>

<template>
  <div class="bg-surface-0 rounded-xs py-1">
    <h2 class="px-3 text-text-2xl font-bold leading-8">{{ title }}</h2>
    <template v-for="list in lists" :key="list.heading">
      <h3 v-if="list.heading" class="px-3 pb-2 pt-5 font-medium leading-6">{{ list.heading }}</h3>
      <ul role="menu" tabindex="0" class="m-0 max-w-40 list-none p-0 outline-none">
        <li
          v-for="item in list.items"
          class="relative my-0.5 first:mt-0"
          :ariaLabel="item.label"
          :key="typeof item.label === 'function' ? item.label() : item.label"
          :ariaCurrent="item.selected ? 'page' : ''"
          role="menuitem"
        >
          <template v-if="item.route">
            <div
            class="relative flex items-center cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
                :class="
                  item.selected
                    ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                    : 'hover:bg-surface-300'
                ">
              <span v-if="item.submenu" class="material-icons-round mr-1 rounded-xs hover:bg-primary-600 transition-colors duration-200" @click.stop="toggleSubmenu()">arrow_drop_down</span>
              <router-link :to="item.route" custom v-slot="{ href, navigate }">
                <a
                  :href="href"
                  @click="navigate"
                  :ariaLabel="item.label"
                  class="w-full"
                >
                  <span class="text-md leading-normal">{{ item.label }}</span>
                </a>
              </router-link>
            </div>
          </template>
          <template v-else>
            <div
            class="relative flex items-center cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
              :class="
                item.selected
                  ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                  : 'hover:bg-surface-300'
              "
            >
            <span v-if="item.submenu" class="material-icons-round mr-1 rounded-xs hover:bg-primary-600 transition-colors duration-200" @click.stop="toggleSubmenu()">arrow_drop_down</span>
            <a
              :href="item.url"
              :ariaLabel="item.label"
              class="w-full"
            >
              <span class="text-md leading-normal">{{ item.label }}</span>
            </a>
            </div>
          </template>
          <ul v-if="item.submenu && showSubmenu" class="ml-4 mt-1">
            <li
              v-for="subitem in item.submenu"
              :key="typeof subitem.label === 'function' ? subitem.label() : subitem.label"
              class="relative my-0.5 first:mt-0"
              :ariaLabel="subitem.label"
              :ariaCurrent="subitem.selected ? 'page' : ''"
              role="menuitem"
            >
              <template v-if="subitem.route">
                <router-link :to="subitem.route" custom v-slot="{ href, navigate }">
                  <a
                    :href="href"
                    @click="navigate"
                    :ariaLabel="subitem.label"
                    class="relative flex cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
                    :class="
                      subitem.selected
                        ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                        : 'hover:bg-surface-300'
                    "
                  >
                    <span class="text-md leading-normal">{{ subitem.label }}</span>
                  </a>
                </router-link>
              </template>
              <template v-else>
                <a
                  :href="subitem.url"
                  :ariaLabel="subitem.label"
                  class="relative flex cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
                  :class="
                    subitem.selected
                      ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                      : 'hover:bg-surface-300'
                  "
                >
                  <span class="text-md leading-normal">{{ subitem.label }}</span>
                </a>
              </template>
            </li>
          </ul>
        </li>
      </ul>
    </template>
  </div>
</template>
